<template>
  <Card class="max-w-2xl">
    <template #header>
      <div class="flex items-center justify-between">
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Profile Information</h2>
        <Button v-if="!isEditing" variant="outline" size="sm" @click="toggleEdit">
          Edit Profile
        </Button>
      </div>
    </template>

    <!-- Success Message -->
    <Alert v-if="updateSuccess" type="success" class="mb-6" @dismiss="clearMessages">
      Profile updated successfully!
    </Alert>

    <!-- Error Message -->
    <Alert v-if="hasError" type="error" class="mb-6" @dismiss="clearMessages">
      {{ error }}
    </Alert>

    <!-- Email Verification Warning -->
    <Alert v-if="!currentUser?.is_verified && !isEditing" type="warning" class="mb-6">
      <div class="flex items-center justify-between">
        <div>
          <p class="text-sm">
            <strong>Email not verified.</strong> Please verify your email address to ensure account
            security.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          @click="requestEmailVerification(currentUser?.email || '')"
          :loading="isRequestingVerification"
        >
          {{ verificationSuccess ? 'Email Sent' : 'Send Verification' }}
        </Button>
      </div>
    </Alert>

    <!-- Verification Success Message -->
    <Alert v-if="verificationSuccess && !showVerificationDialog" type="success" class="mb-6">
      <p class="text-sm">Verification email sent successfully! Please check your inbox.</p>
    </Alert>

    <!-- Verification Error Message -->
    <Alert v-if="verificationError && !showVerificationDialog" type="error" class="mb-6">
      <p class="text-sm">{{ verificationError }}</p>
    </Alert>

    <form @submit.prevent="handleSubmit" class="space-y-6">
      <!-- Email Change Warning (shown when editing) -->
      <Alert v-if="isEditing && currentUser?.is_verified" type="info" class="mb-6">
        <div class="flex items-start space-x-2">
          <svg
            class="w-5 h-5 text-blue-600 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <p class="text-sm text-blue-800">
              <strong>Important:</strong> If you change your email address, your account
              verification status will be reset and you'll need to verify your new email address.
            </p>
          </div>
        </div>
      </Alert>

      <!-- Full Name Field -->
      <div>
        <label
          for="full_name"
          class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2"
        >
          Full Name
        </label>
        <Input
          id="full_name"
          v-model="form.full_name"
          type="text"
          placeholder="Enter your full name"
          :disabled="!isEditing || isLoading"
          :error="fullNameError"
          :helper-text="fullNameError"
          required
          class="w-full"
        />
      </div>

      <!-- Email Field -->
      <div>
        <label for="email" class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
          Email Address
        </label>
        <Input
          id="email"
          v-model="form.email"
          type="email"
          placeholder="Enter your email"
          :disabled="!isEditing || isLoading"
          :error="emailError"
          :helper-text="emailError"
          required
          class="w-full"
        />
      </div>

      <!-- Account Status -->
      <div class="space-y-3">
        <h3 class="text-sm font-medium text-gray-700 dark:text-gray-200">Account Status</h3>
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div class="flex items-center space-x-2">
            <div
              :class="[
                'w-2 h-2 rounded-full',
                currentUser?.is_active ? 'bg-green-500' : 'bg-red-500',
              ]"
            ></div>
            <span class="text-sm text-gray-600 dark:text-gray-300">
              {{ currentUser?.is_active ? 'Active' : 'Inactive' }}
            </span>
          </div>

          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-2">
              <div
                :class="[
                  'w-2 h-2 rounded-full',
                  currentUser?.is_verified ? 'bg-green-500' : 'bg-yellow-500',
                ]"
              ></div>
              <span class="text-sm text-gray-600 dark:text-gray-300">
                {{ currentUser?.is_verified ? 'Verified' : 'Unverified' }}
              </span>
            </div>
            <Button
              v-if="!currentUser?.is_verified && !isEditing"
              variant="outline"
              size="sm"
              @click="requestEmailVerification(currentUser?.email || '')"
              :loading="isRequestingVerification"
              class="text-xs"
            >
              Verify Email
            </Button>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div
        v-if="isEditing"
        class="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-600"
      >
        <Button type="button" variant="outline" @click="handleCancel" :disabled="isLoading">
          Cancel
        </Button>
        <Button type="submit" :loading="isLoading" :disabled="!isFormValid || isLoading">
          Update Profile
        </Button>
      </div>
    </form>
  </Card>

  <!-- Email Verification Dialog -->
  <Dialog
    :open="showVerificationDialog"
    @close="closeVerificationDialog"
    title="Email Verification Required"
    :show-close-button="!isRequestingVerification"
  >
    <template #content>
      <div class="space-y-4">
        <div class="flex items-center space-x-3">
          <div class="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <svg
              class="w-5 h-5 text-blue-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 8l7.89 7.89a2 2 0 002.83 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
          <div>
            <h3 class="text-lg font-medium text-gray-900">Verify Your New Email</h3>
            <p class="text-sm text-gray-600">Your email has been updated successfully.</p>
          </div>
        </div>

        <div class="bg-blue-50 rounded-lg p-4">
          <p class="text-sm text-blue-800 mb-2">
            <strong>Important:</strong> Your email verification status has been reset because you
            changed your email address.
          </p>
          <p class="text-sm text-blue-700">
            We've sent a verification email to <strong>{{ form.email }}</strong
            >. Please check your inbox and click the verification link to verify your new email
            address.
          </p>
        </div>

        <div v-if="verificationError" class="bg-red-50 border border-red-200 rounded-lg p-3">
          <p class="text-sm text-red-700">{{ verificationError }}</p>
        </div>

        <div v-if="verificationSuccess" class="bg-green-50 border border-green-200 rounded-lg p-3">
          <p class="text-sm text-green-700">Verification email sent successfully!</p>
        </div>
      </div>
    </template>

    <template #actions>
      <div class="flex space-x-3">
        <Button
          variant="outline"
          @click="closeVerificationDialog"
          :disabled="isRequestingVerification"
        >
          I'll Verify Later
        </Button>
        <Button
          @click="resendVerificationEmail"
          :loading="isRequestingVerification"
          :disabled="verificationSuccess"
        >
          {{ verificationSuccess ? 'Email Sent' : 'Resend Email' }}
        </Button>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue';
  import { Card, Button, Input, Alert, Dialog } from '@asabdev/asab-ui';
  import { useUserProfile } from '../composables/useUserProfile';
  import { useAuth } from '@/features/auth/composables/useAuth';
  import { profileUpdateSchema, type ProfileUpdateFormData } from '@/lib/validation/users';

  const {
    currentUser,
    isLoading,
    hasError,
    error,
    updateSuccess,
    isEditing,
    updateProfile,
    toggleEdit,
    clearMessages,
  } = useUserProfile();

  const { requestVerifyToken } = useAuth();

  // Form state
  const form = ref<ProfileUpdateFormData>({
    full_name: '',
    email: '',
  });

  const fullNameError = ref('');
  const emailError = ref('');

  // Email verification state
  const showVerificationDialog = ref(false);
  const isRequestingVerification = ref(false);
  const verificationError = ref<string>('');
  const verificationSuccess = ref(false);

  // Sync form with current user
  watch(
    [currentUser, isEditing],
    ([user, editing]) => {
      if (user && !editing) {
        form.value = {
          full_name: user.full_name || '',
          email: user.email || '',
        };
        fullNameError.value = '';
        emailError.value = '';
      }
    },
    { immediate: true }
  );

  // Real-time validation
  const validateFullName = () => {
    const result = profileUpdateSchema.shape.full_name.safeParse(form.value.full_name);
    fullNameError.value = result.success ? '' : result.error.issues[0].message;
    return result.success;
  };

  const validateEmail = () => {
    const result = profileUpdateSchema.shape.email.safeParse(form.value.email);
    emailError.value = result.success ? '' : result.error.issues[0].message;
    return result.success;
  };

  const validateForm = () => {
    const isFullNameValid = validateFullName();
    const isEmailValid = validateEmail();
    return isFullNameValid && isEmailValid;
  };

  watch(
    () => form.value.full_name,
    () => {
      if (!isEditing.value) return;
      validateFullName();
    }
  );

  watch(
    () => form.value.email,
    () => {
      if (!isEditing.value) return;
      validateEmail();
    }
  );

  const isFormValid = computed(() => {
    return (
      form.value.full_name &&
      form.value.email &&
      !fullNameError.value &&
      !emailError.value &&
      (form.value.full_name !== (currentUser.value?.full_name || '') ||
        form.value.email !== (currentUser.value?.email || ''))
    );
  });

  // Email verification methods
  const requestEmailVerification = async (email: string) => {
    try {
      isRequestingVerification.value = true;
      verificationError.value = '';
      verificationSuccess.value = false;

      await requestVerifyToken(email);
      verificationSuccess.value = true;
    } catch (err: unknown) {
      const errorObj = err as { message?: string };
      verificationError.value =
        errorObj.message || 'Failed to send verification email. Please try again.';
    } finally {
      isRequestingVerification.value = false;
    }
  };

  const resendVerificationEmail = () => {
    requestEmailVerification(form.value.email);
  };

  const closeVerificationDialog = () => {
    showVerificationDialog.value = false;
    verificationError.value = '';
    verificationSuccess.value = false;
  };

  // Form handlers
  const handleSubmit = async () => {
    try {
      // Validate form
      if (!validateForm() || !isFormValid.value) return;

      const oldEmail = currentUser.value?.email;
      const newEmail = form.value.email;

      // Update profile
      await updateProfile({
        full_name: form.value.full_name,
        email: newEmail,
      });

      // If email changed and update was successful
      if (updateSuccess.value && oldEmail !== newEmail) {
        // Show verification dialog and request verification
        showVerificationDialog.value = true;

        // Automatically request verification for the new email
        setTimeout(() => {
          requestEmailVerification(newEmail);
        }, 500);

        // Exit edit mode
        setTimeout(() => {
          toggleEdit();
        }, 1500);
      } else if (updateSuccess.value) {
        // If only other fields changed, just exit edit mode
        setTimeout(() => {
          toggleEdit();
        }, 1500);
      }
    } catch (error) {
      // Error is handled by the store and displayed via hasError/error
      console.error('Profile update failed:', error);
    }
  };

  const handleCancel = () => {
    // Reset form to current user data
    if (currentUser.value) {
      form.value.email = currentUser.value.email;
    }
    emailError.value = '';
    clearMessages();
    toggleEdit();
  };
</script>
